<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Quality Dashboards and Reporting as a Service — QikSolve',
  description:
    'How quality reporting and Power BI dashboards help teams turn operational data into useful quality insight and better decisions.',
});

const page = {
  hero: {
    eyebrow: 'Quality dashboards',
    title: 'Turn quality information into clearer decisions',
    summary:
      'Dashboard reporting should help people see what matters, understand performance, and make quality decisions with better context. QikSolve helps teams shape reporting that supports operational control, review, and continuous improvement.',
    cta: { label: 'Discuss your reporting needs', to: '/contact' },
  },
  intro: {
    title: 'The purpose of quality dashboards is better decisions',
    content:
      'Reporting is most useful when it reflects the decisions teams need to make. A good quality dashboard surfaces the right signals, makes exceptions visible, and helps leaders understand whether processes remain in control and where action is needed.',
    items: [
      {
        title: 'Decision-ready visibility',
        description: 'The right dashboard helps people move from raw data to useful operational understanding without losing context.',
      },
      {
        title: 'Clear ownership and accountability',
        description: 'Operational trends, actions, and exceptions are easier to manage when owners and review points are visible.',
      },
      {
        title: 'A practical quality signal',
        description: 'Dashboards should support daily practice, management review, and continual improvement rather than generate information for its own sake.',
      },
    ],
  },
  sections: [
    {
      title: 'What good reporting provides',
      items: [
        {
          title: 'Data integration',
          description: 'Connect operational sources, quality records, and process information into a usable reporting context.',
        },
        {
          title: 'Useful analysis',
          description: 'Highlight trends, exceptions, and leading signals that matter for review and intervention.',
        },
        {
          title: 'Clear visuals',
          description: 'Use approachable, structured visuals that support faster understanding by operational and quality teams.',
        },
        {
          title: 'Secure and proportionate design',
          description: 'Ensure reporting respects access, governance, and the compliance requirements relevant to the organisation.',
        },
      ],
    },
    {
      title: 'Why this matters for regulated work',
      items: [
        {
          title: 'Better management insight',
          description: 'Reporting helps teams see quality performance in context and focus attention on the areas that need action.',
        },
        {
          title: 'Actionable review rhythms',
          description: 'Well-designed dashboards support recurring review, escalation, and follow-up across operational and quality functions.',
        },
        {
          title: 'Sustainable visibility',
          description: 'When reporting is designed around the real decisions being made, it becomes a practical management capability rather than a static display.',
        },
      ],
    },
    {
      title: 'Packages and practical scope',
      items: [
        {
          title: 'Basic package',
          description: 'A focused starting point for a small number of critical quality dashboards and reporting needs.',
        },
        {
          title: 'Quality Plus package',
          description: 'Expanded coverage for teams with more data streams, multiple quality views, and broader reporting requirements.',
        },
        {
          title: 'Quality Unlimited package',
          description: 'A broader, scalable approach for teams that need more reporting breadth and ongoing analytical support.',
        },
      ],
    },
  ],
  callout: {
    title: 'Reporting supports management, not replacement',
    content:
      'Dashboards are a practical tool for visibility and review, but they do not replace the quality decisions, accountability, and governance that responsible teams must still own. The right dashboard helps people act with better information.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related quality pathways',
    items: [
      {
        label: 'Regulatory compliance',
        description: 'How operating practices, evidence, and control come together in regulated work.',
        to: '/product/regulatory-compliance',
      },
      {
        label: 'ISO 9001 in practice',
        description: 'How documented information and review support daily quality practice.',
        to: '/product/iso-9001',
      },
      {
        label: 'SharePoint governance',
        description: 'A practical way to structure information and workflow control in Microsoft 365.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'QikSolve services',
        description: 'Explore the broader service pathways behind the product journey.',
        to: '/services',
      },
      {
        label: 'Power BI and ISO 9001 document-management metrics',
        description: 'Read the existing article about document-management reporting measures.',
        to: '/blog/power-bi-document-management',
      },
    ],
  },
  cta: {
    title: 'Need a clearer reporting picture?',
    content:
      'Bring the questions your team is trying to answer and the quality signals you need to monitor. We can help shape a practical reporting path.',
    label: 'Discuss your reporting needs',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
